import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Кэш ответов на время сессии: показать то, что уже приходило, и молча
 * обновить.
 *
 * Зачем: вкладки статистики и публикаций грузят по несколько секунд, а
 * переключаются между собой постоянно. Без кэша каждое возвращение на вкладку —
 * пустой экран и ожидание, хотя цифры за минуту не изменились.
 *
 * Данные живут в памяти вкладки браузера: перезагрузка страницы их стирает.
 * Это осознанно — статистика не та вещь, которую стоит поднимать с диска
 * недельной давности.
 */

interface Entry {
  data?: unknown;
  /** Когда ответ приехал; 0 — ответа ещё не было. */
  at: number;
  /** Запрос в полёте: второй вызов с тем же ключом ждёт его, а не шлёт свой. */
  promise?: Promise<unknown>;
}

const entries = new Map<string, Entry>();
const listeners = new Map<string, Set<() => void>>();

function notify(key: string): void {
  listeners.get(key)?.forEach((listener) => listener());
}

/** Что лежит в кэше по ключу (или ничего). */
export function readQuery<T>(key: string): { data: T; at: number } | null {
  const entry = entries.get(key);
  // at === 0 — запрос только в полёте, показывать ещё нечего.
  if (!entry || entry.at === 0 || entry.data === undefined) return null;
  return { data: entry.data as T, at: entry.at };
}

export function writeQuery<T>(key: string, data: T): void {
  entries.set(key, { data, at: Date.now() });
  notify(key);
}

/**
 * Забыть всё, что начинается с префикса. Зовём после действий, которые меняют
 * данные на сервере: удалили публикацию — список с ней больше не верен.
 */
export function invalidateQuery(prefix: string): void {
  for (const key of [...entries.keys()]) {
    if (key.startsWith(prefix)) {
      entries.delete(key);
      notify(key);
    }
  }
}

/** Только для тестов: развести состояние между случаями. */
export function clearQueryCache(): void {
  entries.clear();
}

function subscribe(key: string, listener: () => void): () => void {
  const set = listeners.get(key) ?? new Set();
  set.add(listener);
  listeners.set(key, set);

  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(key);
  };
}

/**
 * Один запрос на ключ: параллельные вызовы ждут общий промис, результат
 * попадает в кэш.
 */
export function fetchQuery<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const running = entries.get(key)?.promise;
  if (running) return running as Promise<T>;

  const promise = fetcher()
    .then((data) => {
      entries.set(key, { data, at: Date.now() });
      notify(key);
      return data;
    })
    .catch((error) => {
      // Неудачу не кэшируем: следующий заход должен попробовать снова.
      const entry = entries.get(key);
      if (entry) entries.set(key, { data: entry.data, at: entry.at });
      notify(key);
      throw error;
    });

  const previous = entries.get(key);
  entries.set(key, { data: previous?.data, at: previous?.at ?? 0, promise });

  return promise;
}

export interface CachedQuery<T> {
  /** Данные из кэша или свежие; `null`, пока ничего не приходило. */
  data: T | null;
  error: Error | null;
  /** Первая загрузка: показывать скелетон. */
  isLoading: boolean;
  /** Данные уже есть, но идёт обновление: показывать ненавязчиво. */
  isRefreshing: boolean;
  /** Когда приехали данные, которые видит человек. */
  updatedAt: number | null;
  /** Перезапросить, минуя срок годности. */
  refresh: () => void;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

/**
 * Показывает сохранённый ответ сразу и обновляет его в фоне.
 *
 * @param key    строка, однозначно описывающая запрос (период, страница, фильтр)
 * @param ttlMs  сколько ответ считается свежим; старше — обновляем при заходе
 * @param enabled  false — не ходить на сервер (нет прав, не выбран аккаунт)
 */
export function useCachedQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  { ttlMs = DEFAULT_TTL_MS, enabled = true }: { ttlMs?: number; enabled?: boolean } = {},
): CachedQuery<T> {
  const cached = readQuery<T>(key);
  const [, forceRender] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Свежий fetcher без перезапуска эффекта: он почти всегда новая функция.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => subscribe(key, () => forceRender((n) => n + 1)), [key]);

  const run = useCallback(
    (force: boolean) => {
      if (!enabled) return;

      const entry = readQuery<T>(key);
      const isFresh = entry !== null && Date.now() - entry.at < ttlMs;
      if (isFresh && !force) return;

      setIsFetching(true);
      setError(null);
      fetchQuery(key, fetcherRef.current)
        .catch((e) => setError(e instanceof Error ? e : new Error(String(e))))
        .finally(() => setIsFetching(false));
    },
    [key, ttlMs, enabled],
  );

  useEffect(() => {
    run(false);
  }, [run]);

  return {
    data: cached ? cached.data : null,
    error,
    isLoading: isFetching && !cached,
    isRefreshing: isFetching && cached !== null,
    updatedAt: cached ? cached.at : null,
    refresh: useCallback(() => run(true), [run]),
  };
}
